import React from "react";
import { CCFieldHelp, CCSwitch } from "@pymblecitizencoders/cc-ui";
import { EventDoc } from "../../types/event";
import "./eventManager.css";

interface EventPublicToggleProps {
	event: EventDoc;
	readOnly: boolean;
	onChange: (patch: Partial<EventDoc>) => void;
}

/**
 * VISIBILITY: whether anybody at all, including people with no connection to
 * the school, can see that the event exists. Not the same thing as releasing a
 * permission, which decides whether CARERS OF THE INVITED can respond to it and
 * is the sign-off chain's to decide; an event can be one without being the
 * other. It sits in the sidebar, beside the add start/end time switch.
 *
 * The "?" rather than a `hint` line: what this turns on needs a paragraph and a
 * warning. CCFieldHelp puts it one press away.
 */
export const EventPublicToggle: React.FC<EventPublicToggleProps> = ({
	event,
	readOnly,
	onChange,
}) => {
	/* Built here rather than inline because CCFieldHelp takes the field as a
	   prop, and a switch with four attributes nested inside another component's
	   argument list is harder to read than a name.

	   LIVE SINCE papi v2.44.0, which is what took the `PUBLIC_EVENTS_ENABLED`
	   gate off it. The gate existed for one reason and it is gone: the update
	   model now declares `IsPublic`, so the editor's whole-draft PUT persists
	   what this switch sets instead of having it silently dropped. The API
	   stamps `IsPublicSetBy` / `IsPublicSetAt` on a CHANGE only, so a save that
	   merely carries the flag does not rewrite somebody else's attribution.

	   WHAT IT STILL DOES NOT DO is put the event in front of anybody: there is
	   no unauthenticated route serving public events, so this records the
	   decision and nothing reads it yet. The help modal beside it describes the
	   outside audience the flag is FOR. See `docs/public-events.md`.

	   One consequence of every save now carrying the flag: the API pins a
	   single-event write on the publication state it read, so two people with
	   the editor open and one of them toggling this makes the other's next save
	   a 409. `Event.tsx` answers that with "reload", not "try again". */
	const publicSwitch = (
		<CCSwitch
			id="em-is-public"
			label="Public"
			checked={event.IsPublic === true}
			disabled={readOnly}
			onChange={(IsPublic) => onChange({ IsPublic })}
		/>
	);

	return (
		<CCFieldHelp label="Public" field={publicSwitch}>
			<p>
				A public event can be seen by{" "}
				<strong>anyone outside the school</strong>, without signing in to
				My Pymble. Use it for things the wider community is meant to find:
				an open day, a public concert, a holiday.
			</p>
			<p>
				Leave it off and the event is visible only to people at the school
				it concerns, which is how every event behaves by default.
			</p>
			<p>
				<strong>What goes out with it.</strong> The event&rsquo;s title,
				when it is on, where it is, and the description you have written
				for it.
			</p>
			<p>
				<strong>&ldquo;Where&rdquo; means the exact place.</strong> Not
				only the venue&rsquo;s name, but the address and the map pin saved
				with it, so a stranger can see precisely where to turn up. If this
				event is somewhere you would rather the wider world could not
				point at on a map, leave it off.
			</p>
			<p>
				<strong>The description is the one you typed</strong> — the same
				words parents read alongside the event. It is published as
				written, so read it back as somebody outside the school would
				before turning this on.
			</p>
			<p>
				What never goes out: who is invited, who is attending, the
				requirements, and the approval settings.
			</p>
			<p>
				<strong>This is not the same as releasing a permission.</strong>{" "}
				Carers of the students invited get their permission through the
				sign-off chain, whether or not this is on. Turning this on adds an
				outside audience; it takes nothing away from the invited ones, and
				it does not put the event in front of carers any sooner.
			</p>
			<p>
				Titles and descriptions here are written for people who already
				know the school. A public audience has none of that context, so
				what reads as obvious shorthand inside can read as nothing at all
				outside.
			</p>
		</CCFieldHelp>
	);
};
